import { Container, Graphics } from 'pixi.js';
import { Symbol } from './Symbol';
import { GAME_CONFIG, SymbolData } from '../config/GameConfig';

export class Reel extends Container {
  private symbols: Symbol[] = [];
  private symbolContainer: Container;
  private maskGraphics: Graphics;
  private spinning: boolean = false;
  private spinSpeed: number = 0;
  
  public onSpinComplete?: () => void;
  
  constructor(
    _reelIndex: number,
    symbolData: SymbolData[],
    textures: Map<string, any>
  ) {
    super();
    
    this.symbolContainer = new Container();
    this.addChild(this.symbolContainer);
    
    this.maskGraphics = new Graphics();
    this.createMask();
    this.addChild(this.maskGraphics);
    this.symbolContainer.mask = this.maskGraphics;
    
    this.initializeSymbols(symbolData, textures);
  }
  
  private createMask(): void {
    const { SYMBOL_SIZE, ROWS_COUNT } = GAME_CONFIG;
    const totalHeight = ROWS_COUNT * SYMBOL_SIZE;
    
    console.log('=== MASK DEBUG ===');
    console.log('SYMBOL_SIZE:', SYMBOL_SIZE);
    console.log('ROWS_COUNT:', ROWS_COUNT);
    console.log('totalHeight:', totalHeight);
    console.log('Mask rect: x=' + (-SYMBOL_SIZE/2) + ', y=' + (-totalHeight/2) + ', width=' + SYMBOL_SIZE + ', height=' + totalHeight);
    
    this.maskGraphics.rect(-SYMBOL_SIZE/2, -totalHeight/2, SYMBOL_SIZE, totalHeight);
    this.maskGraphics.fill({ color: 0xFFFFFF });
  }
  
  private initializeSymbols(
    symbolData: SymbolData[],
    textures: Map<string, any>
  ): void {
    const totalSymbols = GAME_CONFIG.ROWS_COUNT + 4;
    
    console.log('=== INITIALIZE REEL ===');
    console.log('Total symbols to create:', totalSymbols);
    console.log('ROWS_COUNT:', GAME_CONFIG.ROWS_COUNT);
    console.log('Symbol positions:');
    
    for (let i = 0; i < totalSymbols; i++) {
      const randomSymbol = this.getRandomSymbol(symbolData);
      const texture = textures.get(randomSymbol.name);
      
      if (!texture) {
        console.error(`Texture not found for symbol: ${randomSymbol.name}`);
        continue;
      }
      
      const symbol = new Symbol(randomSymbol, texture);
      symbol.y = this.getSymbolY(i - 2);
      this.symbols.push(symbol);
      this.symbolContainer.addChild(symbol);
      
      console.log(`  symbols[${i}] = ${randomSymbol.name} (y=${symbol.y.toFixed(1)})`);
    }
    
    console.log('Visible symbols should be at:');
    console.log('  Top (index 2): y=' + this.getSymbolY(0).toFixed(1));
    console.log('  Middle (index 3): y=' + this.getSymbolY(1).toFixed(1));
    console.log('  Bottom (index 4): y=' + this.getSymbolY(2).toFixed(1));
    console.log('Mask covers y range: [' + (-GAME_CONFIG.ROWS_COUNT * GAME_CONFIG.SYMBOL_SIZE / 2) + ' to ' + (GAME_CONFIG.ROWS_COUNT * GAME_CONFIG.SYMBOL_SIZE / 2) + ']');
  }
  
  private getRandomSymbol(symbolData: SymbolData[]): SymbolData {
    const randomIndex = Math.floor(Math.random() * symbolData.length);
    return symbolData[randomIndex];
  }
  
  private getSymbolY(index: number): number {
    const { SYMBOL_SIZE, SYMBOL_SPACING } = GAME_CONFIG;
    const symbolHeight = SYMBOL_SIZE + SYMBOL_SPACING;
    
    // PIXI.js Y axis: negative = UP, positive = DOWN
    // Mask is centered at Y=0 and shows from Y=-144 to Y=+144
    
    // We need symbols to be at:
    // Index 2 (top visible): Y = -100 (UP, near top of mask)
    // Index 3 (middle visible): Y = 0 (center)
    // Index 4 (bottom visible): Y = +100 (DOWN, near bottom of mask)
    
    // Calculate Y position based on index offset from center (index 3)
    // Index 0 = 3 rows above center = -300
    // Index 1 = 2 rows above center = -200
    // Index 2 = 1 row above center = -100 (TOP VISIBLE)
    // Index 3 = center = 0 (MIDDLE VISIBLE)
    // Index 4 = 1 row below center = +100 (BOTTOM VISIBLE)
    // Index 5 = 2 rows below center = +200
    // Index 6 = 3 rows below center = +300
    
    return (index - 3) * symbolHeight;
  }
  
  public spin(finalSymbols: SymbolData[], allSymbols: SymbolData[], textures: Map<string, any>): void {
    this.spinning = true;
    this.spinSpeed = GAME_CONFIG.MAX_SPIN_SPEED;
    
    console.log('=== REEL SPIN DEBUG ===');
    console.log('Final symbols to display (top→bottom):', finalSymbols.map(s => `${s.id}:${s.name}`));
    console.log('Current symbols BEFORE spin:');
    this.symbols.forEach((sym, i) => {
      console.log(`  symbols[${i}] = ${sym?.symbolData?.id}:${sym?.symbolData?.name || 'undefined'} (y=${sym?.y?.toFixed(1)})`);
    });
    console.log('Textures available:', Array.from(textures.keys()).slice(0, 10));
    
    // Symbol array structure:
    // Index 0,1: Above visible area (during spinning)
    // Index 2,3,4: Visible rows (top, middle, bottom)
    // Index 5,6: Below visible area (during spinning)
    
    const visibleStartIndex = 2; // Where visible symbols start
    const visibleCount = 3; // Number of visible rows
    
    // IMPORTANT: During spinning, only use the first 8 symbols (backend IDs 1-8)
    // This ensures spinning symbols match the final result symbols
    const spinSymbols = allSymbols.slice(0, 8);
    console.log('Using spin symbols (backend 1-8):', spinSymbols.map(s => s.name));
    
    // Set random symbols for spinning animation (above and below visible area)
    for (let i = 0; i < visibleStartIndex; i++) {
      const randomSymbol = this.getRandomSymbol(spinSymbols);
      const texture = textures.get(randomSymbol.name);
      
      if (texture && this.symbols[i]) {
        this.symbols[i].symbolData = randomSymbol;
        this.symbols[i].symbolId = randomSymbol.id;
        this.symbols[i].sprite.texture = texture;
      }
    }
    
    for (let i = visibleStartIndex + visibleCount; i < this.symbols.length; i++) {
      const randomSymbol = this.getRandomSymbol(spinSymbols);
      const texture = textures.get(randomSymbol.name);
      
      if (texture && this.symbols[i]) {
        this.symbols[i].symbolData = randomSymbol;
        this.symbols[i].symbolId = randomSymbol.id;
        this.symbols[i].sprite.texture = texture;
      }
    }
    
    // Set the final symbols that will be visible when reels stop
    // finalSymbols[0] = top row, finalSymbols[1] = middle row, finalSymbols[2] = bottom row
    console.log('Setting visible symbols:');
    for (let i = 0; i < visibleCount; i++) {
      const symbolIndex = visibleStartIndex + i;
      const finalSymbol = finalSymbols[i];
      const texture = textures.get(finalSymbol.name);
      
      const rowLabels = ['top', 'middle', 'bottom'];
      console.log(`  symbols[${symbolIndex}] = ${finalSymbol.id}:${finalSymbol.name} (displayed at ${rowLabels[i]} row)`);
      console.log(`    - Texture found: ${texture ? 'YES' : 'NO'}`);
      console.log(`    - Texture URL: ${texture?.textureCacheId || 'N/A'}`);
      
      if (texture && this.symbols[symbolIndex]) {
        this.symbols[symbolIndex].symbolData = finalSymbol;
        this.symbols[symbolIndex].symbolId = finalSymbol.id;
        this.symbols[symbolIndex].sprite.texture = texture;
        console.log(`    - Applied texture for ${finalSymbol.name}`);
      } else {
        console.error(`    - FAILED to apply texture for ${finalSymbol.name}! Texture: ${texture}, Symbol: ${this.symbols[symbolIndex]}`);
      }
    }
    
    console.log('After spin, visible symbols will be:');
    console.log('  Top row (symbols[2]):', this.symbols[2]?.symbolData?.name);
    console.log('  Middle row (symbols[3]):', this.symbols[3]?.symbolData?.name);
    console.log('  Bottom row (symbols[4]):', this.symbols[4]?.symbolData?.name);
  }
  
  public update(delta: number): void {
    if (!this.spinning) return;
    
    const { SYMBOL_SIZE, SYMBOL_SPACING } = GAME_CONFIG;
    const symbolHeight = SYMBOL_SIZE + SYMBOL_SPACING;
    
    this.symbols.forEach(symbol => {
      symbol.y += this.spinSpeed * delta;
    });
    
    // Wrap symbols that go past the bottom
    const startY = this.getSymbolY(0);
    const endY = this.getSymbolY(this.symbols.length - 1);
    const wrapDistance = endY - startY + symbolHeight;
    
    this.symbols.forEach(symbol => {
      if (symbol.y > endY) {
        // Symbol went past bottom, wrap to top
        symbol.y -= wrapDistance;
      }
    });
  }
  
  public stop(): void {
    console.log('=== REEL STOP DEBUG ===');
    console.log('Symbols AFTER spin but BEFORE stop:');
    this.symbols.forEach((sym, i) => {
      console.log(`  symbols[${i}] = ${sym?.symbolData?.name || 'undefined'} (y=${sym?.y?.toFixed(1)})`);
    });
    
    this.spinning = false;
    
    // Reset positions to proper locations
    const startY = this.getSymbolY(0);
    const { SYMBOL_SIZE, SYMBOL_SPACING } = GAME_CONFIG;
    const symbolHeight = SYMBOL_SIZE + SYMBOL_SPACING;
    
    this.symbols.forEach((symbol, index) => {
      symbol.y = startY + index * symbolHeight;
    });
    
    console.log('Symbols AFTER stop (final visible):');
    console.log('  Top row (symbols[2]):', this.symbols[2]?.symbolData?.name);
    console.log('  Middle row (symbols[3]):', this.symbols[3]?.symbolData?.name);
    console.log('  Bottom row (symbols[4]):', this.symbols[4]?.symbolData?.name);
    
    if (this.onSpinComplete) {
      this.onSpinComplete();
    }
  }
  
  public getVisibleSymbols(): Symbol[] {
    return this.symbols.slice(2, 2 + GAME_CONFIG.ROWS_COUNT);
  }
  
  public getSymbolAt(row: number): Symbol {
    return this.symbols[row + 2];
  }
}